import { AlertCircle, CheckCircle, Circle } from 'lucide-react';
import { UseFormWatch } from 'react-hook-form';

import { CoverageFormData } from './types';

// Base tabs available for all coverages
type BaseCoverageTab = 'basic' | 'policy' | 'benefits' | 'claims';
// Extended tabs including contacts for premises coverages
type CoverageTab = BaseCoverageTab | 'contacts';

interface CoverageFormTabsProps {
  activeTab: BaseCoverageTab | CoverageTab;
  onTabChange: (tab: BaseCoverageTab | CoverageTab) => void;
  watch?: UseFormWatch<CoverageFormData>;
  isPremisesCoverage?: boolean;
  propertyContactsCount?: number;
}

// Helper function to determine if a tab has content
const getTabCompletionState = (
  tabId: string,
  formData: CoverageFormData | undefined,
  propertyContactsCount?: number,
): 'complete' | 'partial' | 'empty' => {
  if (!formData) return 'empty';

  switch (tabId) {
    case 'basic': {
      const hasCategory = !!formData.category;
      const hasPolicyType = !!formData.policy_type;
      const hasCarrier = !!formData.carrier?.name;
      const hasInsured = formData.named_insured?.some((item) =>
        item.name?.trim(),
      );

      if (hasCategory && hasPolicyType && hasCarrier && hasInsured)
        return 'complete';
      if (hasCategory || hasPolicyType || hasCarrier || hasInsured)
        return 'partial';
      return 'empty';
    }
    case 'contacts': {
      // For premises coverages - check if property contacts have been added
      if (propertyContactsCount && propertyContactsCount > 0) return 'complete';
      return 'empty';
    }
    case 'policy': {
      const hasVehicles = formData.vehicles && formData.vehicles.length > 0;
      const hasDetails =
        formData.policy_details && formData.policy_details.length > 0;

      if (hasVehicles || hasDetails)
        return hasVehicles && hasDetails ? 'complete' : 'partial';
      return 'empty';
    }
    case 'benefits': {
      const hasBenefits = formData.benefits && formData.benefits.length > 0;
      return hasBenefits ? 'complete' : 'empty';
    }
    case 'claims': {
      const hasClaims = formData.claims && formData.claims.length > 0;
      const hasAdjusters = formData.adjusters && formData.adjusters.length > 0;
      const hasSubrogator = !!formData.subrogator?.name;

      if (hasClaims || hasAdjusters || hasSubrogator) {
        return (hasClaims && hasAdjusters) || hasSubrogator
          ? 'complete'
          : 'partial';
      }
      return 'empty';
    }
    default:
      return 'empty';
  }
};

const getTabIcon = (
  state: 'complete' | 'partial' | 'empty',
  isActive: boolean,
) => {
  if (state === 'complete') {
    return <CheckCircle className="h-4 w-4 text-green-600" />;
  }
  if (state === 'partial') {
    return <AlertCircle className="h-4 w-4 text-amber-500" />;
  }
  return (
    <Circle
      className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}
    />
  );
};

export const CoverageFormTabs = ({
  activeTab,
  onTabChange,
  watch,
  isPremisesCoverage = false,
  propertyContactsCount = 0,
}: CoverageFormTabsProps) => {
  const formData = watch ? watch() : undefined;

  const baseTabs: Array<{
    id: CoverageTab;
    label: string;
    description: string;
    required: boolean;
  }> = [
    {
      id: 'basic',
      label: 'Basic Info',
      description: 'Category, carrier, and policy holder information',
      required: true,
    },
    {
      id: 'policy',
      label: 'Policy Details',
      description: 'Vehicles, policy specifics, and additional details',
      required: false,
    },
    {
      id: 'benefits',
      label: 'Benefits',
      description: 'Coverage types and limits',
      required: false,
    },
    {
      id: 'claims',
      label: 'Claims Info',
      description: 'Active claims, adjusters, and subrogation details',
      required: false,
    },
  ];

  // Insert contacts tab after basic for premises coverages
  const tabs = isPremisesCoverage
    ? [
        baseTabs[0],
        {
          id: 'contacts' as CoverageTab,
          label: 'Property Contacts',
          description: 'Property owners, managers, and other contacts',
          required: false,
        },
        ...baseTabs.slice(1),
      ]
    : baseTabs;

  // Calculate overall progress
  const completedTabs = tabs.filter(
    (tab) =>
      getTabCompletionState(tab.id, formData, propertyContactsCount) ===
      'complete',
  ).length;
  const totalTabs = tabs.length;

  return (
    <div className="space-y-4">
      {/* Progress Indicator */}
      <div className="flex items-center gap-3 text-sm text-gray-600">
        <div className="flex items-center gap-1">
          <div className="text-xs font-medium">Progress:</div>
          <div className="flex items-center gap-1">
            <div className="text-blue-600 font-semibold">{completedTabs}</div>
            <div>of</div>
            <div>{totalTabs}</div>
            <div>sections complete</div>
          </div>
        </div>
        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${(completedTabs / totalTabs) * 100}%` }}
          />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 border-b border-gray-200">
        {tabs.map((tab) => {
          const completionState = getTabCompletionState(
            tab.id,
            formData,
            propertyContactsCount,
          );
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={`group relative px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 min-w-0 ${
                isActive
                  ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                {getTabIcon(completionState, isActive)}
                <div className="flex flex-col items-start min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="truncate">{tab.label}</span>
                    {tab.required && (
                      <span className="text-red-500 text-xs">*</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate max-w-32 hidden sm:block">
                    {tab.description}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Active Tab Description for Mobile */}
      <div className="sm:hidden">
        {tabs.map((tab) => {
          if (tab.id === activeTab) {
            return (
              <div key={tab.id} className="text-xs text-gray-600 px-1">
                {tab.description}
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};
